import { useEffect, useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  BarChart3,
  Eye,
  ExternalLink,
  FileText,
  Heart,
  Loader2,
  MessageCircle,
  Percent,
  Repeat2,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { useStore } from '@/lib/store'
import { fetchAnalytics, syncAnalytics, type AnalyticsPost } from '@/lib/data'
import { PLATFORMS, type PlatformId } from '@/lib/platforms'
import { ChannelAvatar } from '@/components/ChannelAvatar'
import { PageHeader } from '@/components/PageHeader'
import { useUI } from '@/lib/ui'

type RangeId = '7d' | '30d' | '90d'

const RANGES: { id: RangeId; label: string; days: number }[] = [
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
]

const fmt = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  return Math.round(n).toLocaleString()
}

/* ------------------------------------------------------------------ */
/* Real aggregation over the API rows                                  */
/* ------------------------------------------------------------------ */

const engagement = (p: AnalyticsPost): number =>
  (p.metrics?.likes ?? 0) + (p.metrics?.replies ?? 0) + (p.metrics?.reposts ?? 0) + (p.metrics?.quotes ?? 0)

interface Summary {
  posts: number
  impressions: number
  hasImpressions: boolean
  engagements: number
  rate: number | null
}

function summarize(posts: AnalyticsPost[]): Summary {
  let impressions = 0
  let hasImpressions = false
  let engagements = 0
  for (const p of posts) {
    if (p.metrics?.impressions != null) {
      impressions += p.metrics.impressions
      hasImpressions = true
    }
    engagements += engagement(p)
  }
  return {
    posts: posts.length,
    impressions,
    hasImpressions,
    engagements,
    rate: hasImpressions && impressions > 0 ? (engagements / impressions) * 100 : null,
  }
}

const pctDelta = (cur: number, prev: number): number | null =>
  prev > 0 ? Math.round(((cur - prev) / prev) * 1000) / 10 : null

function byDaySeries(posts: AnalyticsPost[], days: number) {
  const buckets = new Map<string, { label: string; impressions: number; engagements: number }>()
  const now = new Date()
  const step = days <= 30 ? 1 : 3
  for (let i = days - 1; i >= 0; i -= step) {
    const d = new Date(now)
    d.setDate(now.getDate() - i)
    const key = d.toDateString()
    buckets.set(key, {
      label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      impressions: 0,
      engagements: 0,
    })
  }
  for (const p of posts) {
    // Snap each post to its bucket (or the nearest earlier one when step > 1).
    const d = new Date(p.publishedAt)
    for (let back = 0; back < step; back++) {
      const probe = new Date(d)
      probe.setDate(d.getDate() - back)
      const b = buckets.get(probe.toDateString())
      if (b) {
        b.impressions += p.metrics?.impressions ?? 0
        b.engagements += engagement(p)
        break
      }
    }
  }
  return [...buckets.values()]
}

/* ------------------------------------------------------------------ */
/* Small UI pieces                                                     */
/* ------------------------------------------------------------------ */

function DeltaBadge({ delta }: { delta: number | null }) {
  if (delta == null) return <span className="text-[11px] text-ink-400">no prior data</span>
  const positive = delta >= 0
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${
        positive ? 'text-accent-green' : 'text-accent-red'
      }`}
    >
      {positive ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
      {positive ? '+' : ''}
      {delta}%
    </span>
  )
}

interface KpiDef {
  key: string
  label: string
  value: string
  delta: number | null
  icon: React.ReactNode
  tint: string
  bg: string
}

function StatCard({ kpi }: { kpi: KpiDef }) {
  return (
    <div className="card flex flex-col gap-3 p-5">
      <div className="flex items-center justify-between">
        <span
          className="grid h-11 w-11 place-items-center rounded-xl"
          style={{ background: kpi.bg, color: kpi.tint }}
        >
          {kpi.icon}
        </span>
        <DeltaBadge delta={kpi.delta} />
      </div>
      <div>
        <div className="font-display text-2xl font-bold tracking-tight text-ink-900">{kpi.value}</div>
        <div className="text-sm text-ink-500">{kpi.label}</div>
      </div>
      <div className="text-[11px] text-ink-400">vs previous period</div>
    </div>
  )
}

const TOOLTIP_STYLE: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e8e5dd',
  borderRadius: 12,
  color: '#1b1a17',
  fontSize: 12,
  boxShadow: '0 12px 34px -10px rgba(25,25,23,0.16)',
}

const AXIS_TICK = { fill: '#8c887f', fontSize: 11 } as const
const GRID_STROKE = '#e8e5dd'

function ChartCard({
  title,
  subtitle,
  children,
  className = '',
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`card p-5 ${className}`}>
      <div className="mb-4">
        <h2 className="font-display text-base font-semibold text-ink-900">{title}</h2>
        {subtitle && <p className="text-xs text-ink-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}

function MetricChip({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-panel border border-line px-2.5 py-1 text-xs font-medium text-ink-500">
      <span className="text-ink-400">{icon}</span>
      {value}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function AnalyticsPage() {
  const channels = useStore((s) => s.channels)
  const openConnect = useUI((s) => s.openConnect)

  const [range, setRange] = useState<RangeId>('30d')
  const [selectedChannel, setSelectedChannel] = useState<string | 'all'>('all')
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  // Fetched at 2x the range so the previous window powers the delta badges.
  const [allPosts, setAllPosts] = useState<AnalyticsPost[]>([])

  const activeRange = RANGES.find((r) => r.id === range) ?? RANGES[1]

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const load = async () => {
      await syncAnalytics()
      const posts = await fetchAnalytics(activeRange.days * 2)
      if (!cancelled) setAllPosts(posts)
    }
    load()
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load analytics')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [activeRange.days])

  const scoped = useMemo(
    () => (selectedChannel === 'all' ? allPosts : allPosts.filter((p) => p.channelId === selectedChannel)),
    [allPosts, selectedChannel],
  )

  const windowStart = Date.now() - activeRange.days * 86_400_000
  const current = useMemo(() => scoped.filter((p) => p.publishedAt >= windowStart), [scoped, windowStart])
  const previous = useMemo(() => scoped.filter((p) => p.publishedAt < windowStart), [scoped, windowStart])

  const summary = useMemo(() => summarize(current), [current])
  const prevSummary = useMemo(() => summarize(previous), [previous])
  const series = useMemo(() => byDaySeries(current, activeRange.days), [current, activeRange.days])

  const channelBars = useMemo(() => {
    const byChannel = new Map<string, { name: string; value: number; color: string }>()
    for (const p of current) {
      const platform = PLATFORMS[p.platform as PlatformId]
      const color = platform && platform.color !== '#ffffff' ? platform.color : '#1b1a17'
      const entry = byChannel.get(p.channelId) ?? { name: p.channelName, value: 0, color }
      entry.value += engagement(p)
      byChannel.set(p.channelId, entry)
    }
    return [...byChannel.values()].sort((a, b) => b.value - a.value)
  }, [current])

  const topPosts = useMemo(
    () =>
      [...current]
        .filter((p) => p.metrics)
        .sort((a, b) => engagement(b) - engagement(a) || (b.metrics?.impressions ?? 0) - (a.metrics?.impressions ?? 0))
        .slice(0, 5),
    [current],
  )

  const pendingMetrics = current.length > 0 && current.every((p) => !p.metrics)
  const channelById = useMemo(() => new Map(channels.map((c) => [c.id, c])), [channels])

  const kpis: KpiDef[] = [
    {
      key: 'impr',
      label: 'Impressions',
      value: summary.hasImpressions ? fmt(summary.impressions) : '-',
      delta: prevSummary.hasImpressions ? pctDelta(summary.impressions, prevSummary.impressions) : null,
      icon: <Eye size={20} />,
      tint: '#2b49d8',
      bg: 'rgba(43,73,216,0.10)',
    },
    {
      key: 'eng',
      label: 'Engagements',
      value: fmt(summary.engagements),
      delta: pctDelta(summary.engagements, prevSummary.engagements),
      icon: <Heart size={20} />,
      tint: '#d8407f',
      bg: 'rgba(216,64,127,0.10)',
    },
    {
      key: 'posts',
      label: 'Posts published',
      value: fmt(summary.posts),
      delta: pctDelta(summary.posts, prevSummary.posts),
      icon: <FileText size={20} />,
      tint: '#7c5cff',
      bg: 'rgba(124,92,255,0.10)',
    },
    {
      key: 'rate',
      label: 'Engagement rate',
      value: summary.rate != null ? summary.rate.toFixed(1) + '%' : '-',
      delta:
        summary.rate != null && prevSummary.rate != null && prevSummary.rate > 0
          ? Math.round(((summary.rate - prevSummary.rate) / prevSummary.rate) * 1000) / 10
          : null,
      icon: <Percent size={20} />,
      tint: '#1f9d57',
      bg: 'rgba(31,157,87,0.10)',
    },
  ]

  const rangeSelector = (
    <div className="flex items-center gap-1 rounded-full border border-line bg-panel p-1">
      {RANGES.map((r) => (
        <button
          key={r.id}
          onClick={() => setRange(r.id)}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
            range === r.id ? 'bg-brand-500 text-white shadow-glow' : 'text-ink-500 hover:text-ink-900'
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  )

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Analytics"
        subtitle="Real performance pulled from every connected network"
        icon={<BarChart3 size={20} />}
        actions={rangeSelector}
      />

      {channels.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <span className="grid h-16 w-16 place-items-center rounded-2xl bg-panel border border-line text-ink-400">
            <BarChart3 size={32} />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-ink-900">No analytics yet</h2>
            <p className="mt-1 text-sm text-ink-500">Connect a channel to start tracking performance</p>
          </div>
          <button className="btn-primary" onClick={openConnect}>
            Connect a channel
          </button>
        </div>
      ) : loading ? (
        <div className="grid flex-1 place-items-center">
          <Loader2 className="animate-spin text-brand-400" size={26} />
        </div>
      ) : error ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="text-sm font-medium text-ink-700">Could not load analytics</p>
          <p className="text-xs text-ink-400">{error}</p>
        </div>
      ) : current.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <span className="grid h-16 w-16 place-items-center rounded-2xl bg-panel border border-line text-ink-400">
            <BarChart3 size={32} />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-ink-900">Nothing published in this period</h2>
            <p className="mt-1 text-sm text-ink-500">
              Once a post goes live, its impressions and engagement show up here.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {/* Channel filter row */}
          <div className="flex flex-wrap items-center gap-2 animate-slide-up">
            <button
              onClick={() => setSelectedChannel('all')}
              className={`chip border transition ${
                selectedChannel === 'all'
                  ? 'border-brand-200 bg-brand-50 text-brand-600'
                  : 'border-line text-ink-600 hover:border-ink-300'
              }`}
            >
              All channels
            </button>
            {channels.map((c) => {
              const active = selectedChannel === c.id
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedChannel(c.id)}
                  className={`chip gap-2 border transition ${
                    active
                      ? 'border-brand-200 bg-brand-50 text-brand-600'
                      : 'border-line text-ink-600 hover:border-ink-300'
                  }`}
                >
                  <ChannelAvatar channel={c} size="xs" showBadge={false} />
                  {c.name}
                </button>
              )
            })}
          </div>

          {pendingMetrics && (
            <div className="card flex items-center gap-3 border-brand-200 bg-brand-50 p-4 text-sm text-brand-600 animate-slide-up">
              <Loader2 size={16} className="animate-spin" />
              Metrics for your latest posts are being collected. First numbers usually land within an hour of publishing.
            </div>
          )}

          {/* KPI stat cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 animate-slide-up">
            {kpis.map((k) => (
              <StatCard key={k.key} kpi={k} />
            ))}
          </div>

          {/* Performance over time */}
          <ChartCard
            title="Performance over time"
            subtitle={`Impressions & engagements by publish day · last ${activeRange.label}`}
            className="animate-slide-up"
          >
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="impGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2b49d8" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#2b49d8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="engGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1f9d57" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#1f9d57" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID_STROKE} vertical={false} />
                <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => fmt(v)}
                  width={44}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#6f6b63', marginBottom: 4 }}
                  cursor={{ stroke: '#c8c4ba', strokeWidth: 1 }}
                  formatter={(value: number, name: string) => [fmt(value), name]}
                />
                <Area
                  type="monotone"
                  dataKey="impressions"
                  name="Impressions"
                  stroke="#2b49d8"
                  strokeWidth={2}
                  fill="url(#impGrad)"
                />
                <Area
                  type="monotone"
                  dataKey="engagements"
                  name="Engagements"
                  stroke="#1f9d57"
                  strokeWidth={2}
                  fill="url(#engGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Two side-by-side cards */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 animate-slide-up">
            <ChartCard title="Engagement by channel" subtitle="Total engagements per channel">
              {channelBars.length === 0 ? (
                <p className="py-16 text-center text-sm text-ink-400">No engagement recorded yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={channelBars} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid stroke={GRID_STROKE} horizontal={false} />
                    <XAxis
                      type="number"
                      tick={AXIS_TICK}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v: number) => fmt(v)}
                    />
                    <YAxis type="category" dataKey="name" tick={AXIS_TICK} tickLine={false} axisLine={false} width={88} />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      cursor={{ fill: 'rgba(43,73,216,0.06)' }}
                      formatter={(value: number) => [fmt(value), 'Engagements']}
                    />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
                      {channelBars.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Engagement breakdown" subtitle="Likes, replies and reposts this period">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={[
                    {
                      name: 'Likes',
                      value: current.reduce((n, p) => n + (p.metrics?.likes ?? 0), 0),
                      color: '#d8407f',
                    },
                    {
                      name: 'Replies',
                      value: current.reduce((n, p) => n + (p.metrics?.replies ?? 0), 0),
                      color: '#2b49d8',
                    },
                    {
                      name: 'Reposts',
                      value: current.reduce((n, p) => n + (p.metrics?.reposts ?? 0) + (p.metrics?.quotes ?? 0), 0),
                      color: '#1f9d57',
                    },
                  ]}
                  margin={{ top: 4, right: 16, left: -12, bottom: 4 }}
                >
                  <CartesianGrid stroke={GRID_STROKE} vertical={false} />
                  <XAxis dataKey="name" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                  <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={(v: number) => fmt(v)} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    cursor={{ fill: 'rgba(43,73,216,0.06)' }}
                    formatter={(value: number) => [fmt(value), 'Total']}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={44}>
                    {['#d8407f', '#2b49d8', '#1f9d57'].map((c) => (
                      <Cell key={c} fill={c} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Top posts */}
          <ChartCard title="Top posts" subtitle="Your best performers this period" className="animate-slide-up">
            {topPosts.length === 0 ? (
              <p className="py-10 text-center text-sm text-ink-400">
                No measured posts yet. Metrics appear shortly after a post goes live.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {topPosts.map((post, i) => {
                  const channel = channelById.get(post.channelId)
                  return (
                    <li key={post.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                      <span className="w-5 shrink-0 text-center font-display text-sm font-bold text-ink-300">
                        {i + 1}
                      </span>
                      {channel ? (
                        <ChannelAvatar channel={channel} size="sm" />
                      ) : (
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-panel border border-line text-ink-400">
                          <MessageCircle size={14} />
                        </span>
                      )}
                      <p className="min-w-0 flex-1 truncate text-sm text-ink-800">{post.content || '(no text)'}</p>
                      <div className="hidden shrink-0 items-center gap-2 sm:flex">
                        {post.metrics?.impressions != null && (
                          <MetricChip icon={<Eye size={12} />} value={fmt(post.metrics.impressions)} />
                        )}
                        <MetricChip icon={<Heart size={12} />} value={fmt(post.metrics?.likes ?? 0)} />
                        <MetricChip icon={<MessageCircle size={12} />} value={fmt(post.metrics?.replies ?? 0)} />
                        <MetricChip icon={<Repeat2 size={12} />} value={fmt(post.metrics?.reposts ?? 0)} />
                      </div>
                      {post.url && (
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noreferrer"
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-line text-ink-400 transition-colors hover:text-ink-900"
                          title="Open live post"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </ChartCard>
        </div>
      )}
    </div>
  )
}
